import {
  MinusCircleOutlined,
  PlusCircleOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import {
  Affix,
  Button,
  Card,
  Col,
  Divider,
  Form,
  Input,
  InputNumber,
  message,
  Row,
  Select,
  Space,
} from "antd";
import CustomDatePicker from "common/CustomDatePicker";
import moment from "moment";
import { forwardRef, useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ACCEPTANCE_B2BLC_LIST,
  B2BLC_INVOICE_LIST,
  CHALLAN_LIST,
  EXPORT_SHIPMENT_FOC_INVOICE_LIST,
  EXPORT_SHIPMENT_LC_INVOICE_LIST,
  EXPORT_VEHICLE_ASSIGNMENT,
  EXPORT_VEHICLE_ASSIGNMENT_VEHICLE_TYPES,
  FOC_TYPE_INVOICE_LIST,
  IMPORT_VEHICLE_ASSIGNMENT,
  IMPORT_VEHICLE_ASSIGNMENT_VEHICLE_TYPES,
  SALES_CONTRACT_LIST,
} from "../../../apiServices/API_ENDPOINTS";
import { getData, postData, putData } from "../../../apiServices/common";
import HandleNotification from "../../../common/Notification";
import { isArrayAndHasValue } from "../../../utils/functions";
import Header from "../../Common/Header";
import ShipmentTransportCharges, {
  transportChargeFormValues,
  transportChargePayload,
} from "../ShipmentTransportCharges";

// Constants
const transporter = [
  { id: 1, value: "Self Transport" },
  { id: 2, value: "Raj Transport" },
  { id: 3, value: "SR Enterprise" },
  { id: 4, value: "Vendor" },
];

const importer_names = [
  { id: 1, value: "CONCORDE GARMENTS LTD." },
  { id: 2, value: "OSMAN INTERLININGS LTD." },
  { id: 2, value: "OSMAN ACCESSORIES" },
  { id: 2, value: "OTL-DOUBLE GULL MFG. CO. LTD" },
  { id: 2, value: "LILY APPARELS LTD" },
  { id: 2, value: "SSL WIRELESS" },
  { id: 2, value: "REFLECTIONS" },
  { id: 2, value: "STAINED GLASS OVERLAY" },
  { id: 2, value: "BIDDYUT LTD." },
];

const place_of_loading = [
  { id: 1, value: "CTG" },
  { id: 2, value: "AIRPORT" },
  { id: 3, value: "DEPZ" },
  { id: 4, value: "AEPZ" },
  { id: 5, value: "COMILLA EPZ" },
  { id: 6, value: "BENAPOL" },
  { id: 7, value: "ICD" },
  { id: 8, value: "KAMALAPUR" },
  { id: 9, value: "OTHERS" },
];

const place_of_arrival = [
  { id: 1, value: "MIRPUR" },
  { id: 1, value: "SAVAR EPZ" },
  { id: 1, value: "ASHULIA" },
  { id: 1, value: "ESKATON" },
  { id: 1, value: "KAWRANBAZAR" },
  { id: 1, value: "BARIDHARA" },
  { id: 1, value: "OTHERS" },
];

const default_vehicle_types = ["5 Ton", "3 Ton", "2 Ton", "1 Ton"];

const mergeVehicleTypes = (list, ...types) => {
  const merged = [...list];
  types.forEach((type) => {
    const trimmed = type?.trim();
    if (
      trimmed &&
      !merged.some((item) => item.toLowerCase() === trimmed.toLowerCase())
    ) {
      merged.push(trimmed);
    }
  });
  return merged;
};

const vehicle_sizes = [
  { value: "8 FT" },
  { value: "10 FT" },
  { value: "12 FT" },
  { value: "14 FT" },
  { value: "15 FT" },
  { value: "16 FT" },
  { value: "18 FT" },
  { value: "20 FT" },
  { value: "23 FT" },
  { value: "Others" },
];

const ImportVehicleAssignmentForm = forwardRef((props, ref) => {
  // Props
  const { editMode } = props;

  // States
  const [b2bLcList, setB2bLcList] = useState([]);
  const [salesContractList, setSalesContractList] = useState(null);
  const [challanList, setChallanList] = useState(null);
  // assigned vehicles managed by Form.List; initial fallback set into form where needed
  const [invoiceList, setInvoiceList] = useState([]);
  const [updatePerformed, setUpdatePerformed] = useState(false);
  const [vehicleTypeList, setVehicleTypeList] = useState(default_vehicle_types);
  const [newVehicleTypeInput, setNewVehicleTypeInput] = useState("");
  const [savedExporterName, setSavedExporterName] = useState(null);
  // TODO: populate from the gate pass API once it is available
  const [gatePassList] = useState([]);

  // Form data state
  const [formData, setFormData] = useState({
    type_of_shipment: null,
    b2b_lc_id: null,
    sales_contract_id: null,
    import_details_id: null,
    loading_qty: null,
    loading_cbm: null,
    loading_weight: null,
  });

  // Router
  const navigate = useNavigate();
  const { assignmentId } = useParams();
  const location = useLocation();
  const isExport = location?.pathname?.startsWith("/export/");
  const shipmentLabel = isExport ? "Export" : "Import";
  const listPath = isExport
    ? "/export/vehicle-assignment"
    : "/import/vehicle-assignment";
  const loadingPlaceOptions = isExport ? place_of_arrival : place_of_loading;
  const arrivalPlaceOptions = isExport ? place_of_loading : place_of_arrival;
  const loggedInUserName = localStorage.getItem("name") || "";
  const exporterNameOptions = [
    ...new Set([loggedInUserName, savedExporterName].filter(Boolean)),
  ];
  const assignmentApi = isExport
    ? EXPORT_VEHICLE_ASSIGNMENT
    : IMPORT_VEHICLE_ASSIGNMENT;
  const lcInvoiceListApi = isExport
    ? EXPORT_SHIPMENT_LC_INVOICE_LIST
    : B2BLC_INVOICE_LIST;
  const focInvoiceListApi = isExport
    ? EXPORT_SHIPMENT_FOC_INVOICE_LIST
    : FOC_TYPE_INVOICE_LIST;
  const vehicleTypesApi = isExport
    ? EXPORT_VEHICLE_ASSIGNMENT_VEHICLE_TYPES
    : IMPORT_VEHICLE_ASSIGNMENT_VEHICLE_TYPES;

  // Antd
  const [form] = Form.useForm();
  const { Option } = Select;

  const getSelectedVehicleAssignment = useCallback(
    async (id) => {
      const query = `${assignmentApi}/${id}`;
      const response = await getData(query);

      if (response && response?.data?.code === 200) {
        const data = response?.data?.data;

        // Set form data
        setFormData({
          type_of_shipment: data?.type_of_shipment,
          b2b_lc_id: data?.b2b_lc_id,
          sales_contract_id: data?.sales_contract_id,
          import_details_id: data?.import_details_id,
          loading_qty: data?.loading_qty,
          loading_cbm: data?.loading_cbm,
          loading_weight: data?.loading_weight,
        });

        // Set vehicle data
        if (data?.assigned_vehicles && data?.assigned_vehicles.length > 0) {
          const vehiclesWithIds = data.assigned_vehicles.map(
            (vehicle, index) => ({
              ...vehicle,
              unique_id: index,
              // convert date strings to moment objects for the datepickers
              empty_vehicle_arrival_time: vehicle.empty_vehicle_arrival_time
                ? moment(vehicle.empty_vehicle_arrival_time)
                : null,
              dispatch_time: vehicle.dispatch_time
                ? moment(vehicle.dispatch_time)
                : null,
              unloading_date: vehicle.unloading_date
                ? moment(vehicle.unloading_date)
                : null,
              arrival_date: vehicle.arrival_date
                ? moment(vehicle.arrival_date)
                : null,
            }),
          );

          // Ensure challan items are in the list for edit mode display
          const uniqueChallanIds = [
            ...new Set(
              data.assigned_vehicles
                .filter((vehicle) => vehicle.challan_id)
                .map((vehicle) => vehicle.challan_id),
            ),
          ];

          if (uniqueChallanIds.length > 0) {
            const challanPromises = uniqueChallanIds.map((challanId) =>
              getData(`${CHALLAN_LIST}?id=${challanId}`),
            );
            const challanResponses = await Promise.all(challanPromises);
            const challanData = challanResponses
              .map((res) => res?.data?.data?.data)
              .flat();

            setChallanList(challanData);
          }

          setVehicleTypeList((prev) =>
            mergeVehicleTypes(
              prev,
              ...data.assigned_vehicles.map((vehicle) => vehicle.vehicle_type),
            ),
          );

          // Set assigned vehicles into form so Form.List can manage them
          form.setFieldsValue({ assigned_vehicles: vehiclesWithIds });

          if (isExport) {
            const exporterName = data.assigned_vehicles[0]?.importer_name;
            setSavedExporterName(exporterName || null);
            form.setFieldsValue({ exporter_name: exporterName });
          }
        }

        // Handle loading invoice list based on type of shipment
        if (data?.type_of_shipment === "FOC") {
          // For FOC, load FOC invoice list
          const focResponse = await getData(`${focInvoiceListApi}`);
          if (focResponse && focResponse?.data?.code === 200) {
            setInvoiceList(focResponse?.data?.data || []);
          }
        } else if (data?.b2b_lc_id) {
          // For non-FOC, load LC invoice list
          const invoiceResponse = await getData(
            `${lcInvoiceListApi}/${data.b2b_lc_id}`,
          );
          if (invoiceResponse && invoiceResponse?.status === 200) {
            setInvoiceList(invoiceResponse?.data?.data || []);
          }
        }

        // Ensure sales contract is in the list
        if (data?.sales_contract_info) {
          setSalesContractList((prevList) => {
            const updatedList = prevList || [];
            const contractExists = updatedList.find(
              (item) => item?.id === data.sales_contract_info.id,
            );

            if (!contractExists) {
              return [
                ...updatedList,
                {
                  id: data.sales_contract_info.id,
                  reference_no: data.sales_contract_info.reference_no,
                },
              ];
            }
            return updatedList;
          });
        }

        // Set form values
        form.setFieldsValue({
          type_of_shipment: data?.type_of_shipment,
          b2b_lc_id: data?.b2b_lc_id,
          sales_contract_id: data?.sales_contract_id,
          import_details_id: data?.import_details_id,
          export_carton: data?.export_carton,
          garment_piece_qty: data?.garment_piece_qty,
          loading_qty: data?.loading_qty,
          loading_cbm: data?.loading_cbm,
          loading_weight: data?.loading_weight,
          ...transportChargeFormValues(data),
        });
      } else {
        HandleNotification(
          "error",
          "bottomRight",
          "Error getting vehicle assignment data",
          null,
        );
      }
    },
    [form, assignmentApi, focInvoiceListApi, lcInvoiceListApi, isExport],
  );

  useEffect(() => {
    const lookupsReady =
      isExport || (b2bLcList.length > 0 && salesContractList);
    if (editMode && assignmentId && lookupsReady) {
      getSelectedVehicleAssignment(assignmentId);
    }
  }, [
    isExport,
    editMode,
    assignmentId,
    getSelectedVehicleAssignment,
    b2bLcList,
    salesContractList,
  ]);

  useEffect(() => {
    if (isExport && !editMode) {
      form.setFieldsValue({
        type_of_shipment: "Export",
        exporter_name: loggedInUserName || undefined,
      });
    }
  }, [isExport, editMode, loggedInUserName, form]);

  const onChangeFormValue = (value, keyName) => {
    setFormData((prev) => ({
      ...prev,
      [keyName]: value,
    }));

    // Handle type_of_shipment changes
    if (keyName === "type_of_shipment") {
      // Reset dependent fields when type of shipment changes
      setFormData((prev) => ({
        ...prev,
        b2b_lc_id: null,
        sales_contract_id: null,
        import_details_id: null,
      }));

      // Reset form fields
      form.setFieldsValue({
        b2b_lc_id: null,
        sales_contract_id: null,
        import_details_id: null,
      });

      // Reset invoice list
      setInvoiceList([]);

      // If FOC is selected, get FOC invoice list
      if (value === "FOC") {
        getFOCInvoiceList();
      }
    }
  };

  const onChangeVehicleValue = (value, index, keyName) => {
    // Update value inside Form.List 'assigned_vehicles' so validation and form state stay in sync
    const currentList = form.getFieldValue("assigned_vehicles") || [];
    const idx = currentList.findIndex((i) => i?.unique_id === index);
    if (idx >= 0) {
      currentList[idx] = { ...currentList[idx], [keyName]: value };
      form.setFieldsValue({ assigned_vehicles: currentList });
    } else {
      // fallback: if unique_id not found, try numeric index
      const fallback = [...currentList];
      fallback[index] = { ...(fallback[index] || {}), [keyName]: value };
      form.setFieldsValue({ assigned_vehicles: fallback });
    }
  };

  const getB2BLcList = async () => {
    const query = `${ACCEPTANCE_B2BLC_LIST}`;
    const response = await getData(query);

    if (response && response?.data?.code === 200) {
      setB2bLcList(response?.data?.data);
    }
  };

  const getAllChallan = async (filterValues) => {
    const { name } = filterValues || {};
    const query = `${CHALLAN_LIST}`;
    const bodyData = {
      ...(name && { name: name }),
    };

    const response = await getData(query, false, bodyData);

    if (response && response?.data?.code === 200) {
      setChallanList(response?.data?.data?.data);
    } else {
      message.error("Error getting challan data");
    }
  };

  const getSalesContractList = async (filterValues) => {
    const query = `${SALES_CONTRACT_LIST}`;
    const bodyData = {
      per_page: 10,
      ...(filterValues &&
        filterValues?.name && {
          reference_no: filterValues?.name,
        }),
    };
    let response = await getData(query, false, bodyData);

    if (response && response?.data?.code === 200) {
      setSalesContractList(response?.data?.data?.data);
    } else {
      setSalesContractList([]);
    }
  };

  const getInvoiceList = async (lc_id) => {
    const getLC = b2bLcList.find((item) => item.id === lc_id);

    const response = await getData(`${lcInvoiceListApi}/${lc_id}`);

    if (response && response?.status === 200) {
      // Set invoice list in formData state
      setInvoiceList(response?.data?.data || []);

      // Update form data with LC info
      setFormData((prev) => ({
        ...prev,
        b2b_lc_id: lc_id,
        import_details_id: null, // Reset invoice selection
      }));

      // Set form field value
      form.setFieldsValue({
        b2b_lc_id: lc_id,
        import_details_id: null,
      });

      if (getLC && formData.type_of_shipment !== "FOC") {
        const salesContract = getLC?.sale_contract_info;
        const foundSalesContractList = salesContractList?.find(
          (item) => item?.id === salesContract?.id,
        );

        if (!foundSalesContractList && salesContract) {
          // Add the sales contract to the list so it can be displayed
          setSalesContractList((prevList) => {
            const updatedList = prevList || [];
            return [
              ...updatedList,
              {
                id: salesContract.id,
                reference_no: salesContract.reference_no,
              },
            ];
          });
        }

        // Update form data and form fields with sales contract
        setFormData((prev) => ({
          ...prev,
          sales_contract_id: salesContract?.id || null,
        }));

        form.setFieldsValue({
          sales_contract_id: salesContract?.id || null,
        });
      }
    } else {
      HandleNotification(
        "error",
        "bottomRight",
        "Error fetching invoice list",
        null,
      );
    }
  };

  const getFOCInvoiceList = async () => {
    const response = await getData(`${focInvoiceListApi}`);

    if (response && response?.data?.code === 200) {
      // Set FOC invoice list
      setInvoiceList(response?.data?.data || []);

      // Reset LC related fields for FOC
      setFormData((prev) => ({
        ...prev,
        b2b_lc_id: null,
        import_details_id: null,
      }));

      form.setFieldsValue({
        b2b_lc_id: null,
        import_details_id: null,
      });
    } else {
      HandleNotification(
        "error",
        "bottomRight",
        "Error fetching FOC invoice list",
        null,
      );
    }
  };

  const onSelectInvoice = (invoiceId) => {
    const foundInvoice = invoiceList.find((item) => item.id === invoiceId);

    if (foundInvoice) {
      // Update form data with invoice selection
      setFormData((prev) => ({
        ...prev,
        import_details_id: invoiceId,
      }));

      // Set form field value
      form.setFieldsValue({
        import_details_id: invoiceId,
      });
    }
  };

  const getVehicleTypes = async () => {
    const response = await getData(vehicleTypesApi);

    if (response && response?.data?.code === 200) {
      setVehicleTypeList((prev) =>
        mergeVehicleTypes(prev, ...(response?.data?.data || [])),
      );
    }
  };

  const addVehicleType = (rowIndex) => {
    const type = newVehicleTypeInput?.trim();
    if (!type) return;

    const existing = vehicleTypeList.find(
      (item) => item.toLowerCase() === type.toLowerCase(),
    );
    setVehicleTypeList((prev) => mergeVehicleTypes(prev, type));
    onChangeVehicleValue(existing || type, rowIndex, "vehicle_type");
    setNewVehicleTypeInput("");
  };

  // Effects
  useEffect(() => {
    if (!isExport) {
      getB2BLcList();
      getSalesContractList();
    }
    getAllChallan();
    getVehicleTypes();
  }, []);

  // Handle sales contract list updates when form data changes
  useEffect(() => {
    // Early return if conditions not met or update already performed
    if (
      updatePerformed ||
      !editMode ||
      !assignmentId ||
      !formData ||
      !salesContractList?.length
    ) {
      return;
    }

    // Mark update as performed for edit mode
    setUpdatePerformed(true);
  }, [editMode, assignmentId, formData, salesContractList, updatePerformed]);

  // add/remove are provided by Form.List; no extra helper required

  const onFinishAssignment = async (values) => {
    // Helper function to convert empty values to null
    const nullIfEmpty = (value) => {
      // Handle 0 as a valid number
      if (value === 0) return 0;
      // Otherwise return value or null
      return value || null;
    };

    // Create request body
    const bodyData = {
      type_of_shipment: isExport
        ? "Export"
        : nullIfEmpty(values.type_of_shipment),
      ...(isExport
        ? {
            export_carton: nullIfEmpty(values.export_carton),
            garment_piece_qty: nullIfEmpty(values.garment_piece_qty),
          }
        : {
            b2b_lc_id: nullIfEmpty(values.b2b_lc_id),
            sales_contract_id: nullIfEmpty(values.sales_contract_id),
            import_details_id: nullIfEmpty(values.import_details_id),
          }),
      loading_qty: nullIfEmpty(values.loading_qty),
      loading_cbm: nullIfEmpty(values.loading_cbm),
      loading_weight: nullIfEmpty(values.loading_weight),
      ...transportChargePayload(values),
      assigned_vehicles: (values.assigned_vehicles || []).map((vehicle) => {
        const { unique_id, ...vehicleData } = vehicle;
        // convert moment objects to ISO strings if present
        const convertDate = (val) => {
          if (!val) return null;
          // moment object
          if (val && typeof val === "object" && val?.toISOString)
            return val.toISOString();
          // string
          return val;
        };

        return {
          vehicle_number: nullIfEmpty(vehicleData.vehicle_number),
          transporter: nullIfEmpty(vehicleData.transporter),
          vehicle_size: nullIfEmpty(vehicleData.vehicle_size),
          vehicle_type: nullIfEmpty(vehicleData.vehicle_type),
          driver_name: nullIfEmpty(vehicleData.driver_name),
          driver_cell_number: nullIfEmpty(vehicleData.driver_cell_number),
          driving_license_number: nullIfEmpty(
            vehicleData.driving_license_number,
          ),
          driver_nid_number: nullIfEmpty(vehicleData.driver_nid_number),
          loading_place: nullIfEmpty(vehicleData.loading_place),
          empty_vehicle_arrival_time: convertDate(
            vehicleData.empty_vehicle_arrival_time,
          ),
          dispatch_time: convertDate(vehicleData.dispatch_time),
          challan_id: nullIfEmpty(vehicleData.challan_id),
          gate_pass_no: nullIfEmpty(vehicleData.gate_pass_no),
          security_bolt_seal_number: nullIfEmpty(
            vehicleData.security_bolt_seal_number,
          ),
          gprs_tracking_number: nullIfEmpty(vehicleData.gprs_tracking_number),
          delivery_place: nullIfEmpty(vehicleData.delivery_place),
          arrival_date: convertDate(vehicleData.arrival_date),
          importer_name: nullIfEmpty(
            isExport ? values.exporter_name : vehicleData.importer_name,
          ),
        };
      }),
    };

    const addQuery = assignmentApi;
    const editQuery = `${assignmentApi}/${assignmentId}`;

    if (editMode) {
      const response = await putData(editQuery, bodyData);

      if (response && response?.code === 200) {
        HandleNotification("success", "bottomRight", response?.message, null);
        navigate(listPath);
      } else {
        HandleNotification("error", "bottomRight", response?.message, null);
      }
    } else {
      const response = await postData(addQuery, bodyData);

      if (response && response?.code === 200) {
        HandleNotification("success", "bottomRight", response?.message, null);
        navigate(listPath);
      } else {
        HandleNotification("error", "bottomRight", response?.message, null);
      }
    }
  };

  const discardForm = () => {
    navigate(listPath);
  };

  return (
    <>
      <Affix offsetTop={0}>
        <Card className="p-fixed">
          <Header
            title={`${editMode ? "Update" : "Add"} ${
              isExport
                ? "Export Shipment for Multiple Vehicle"
                : "Vehicle Assignment"
            }`}
            actions={[
              <Affix offsetTop={20} key="discard">
                <Button size="small" danger onClick={() => discardForm()}>
                  Discard
                </Button>
              </Affix>,
              <Affix offsetTop={20} key="save">
                <Button
                  size="small"
                  type="primary"
                  onClick={() => form.submit()}
                >
                  Save
                </Button>
              </Affix>,
            ]}
          />
        </Card>
      </Affix>
      <div className="containt-body pt-3">
        <Form
          layout="vertical"
          form={form}
          name="vehicle-assignment-form"
          onFinish={onFinishAssignment}
        >
          {/* Main Assignment Information */}
          <Card title="Assignment Information">
            <Row gutter={6}>
              <Col span={isExport ? 4 : 6}>
                <Form.Item
                  label="Type of Shipment"
                  name="type_of_shipment"
                  rules={[
                    {
                      required: true,
                      message: "Please Select Type of Shipment",
                    },
                  ]}
                >
                  {isExport ? (
                    <Select placeholder="Type of Shipment" size="small" disabled>
                      <Option value="Export">Export</Option>
                    </Select>
                  ) : (
                    <Select
                      placeholder="Type of Shipment"
                      size="small"
                      onChange={(value) =>
                        onChangeFormValue(value, "type_of_shipment")
                      }
                    >
                      <Option value="LC">LC</Option>
                      <Option value="RTGS">RTGS</Option>
                      <Option value="FOC">FOC</Option>
                      <Option value="TT">TT</Option>
                    </Select>
                  )}
                </Form.Item>
              </Col>
              {isExport ? (
                <>
                  <Col span={4}>
                    <Form.Item
                      label="Export Carton"
                      name="export_carton"
                      rules={[
                        {
                          required: true,
                          message: "Please enter Export Carton",
                        },
                      ]}
                    >
                      <InputNumber
                        placeholder="Export Carton"
                        size="small"
                        min={0}
                        precision={0}
                        style={{ width: "100%" }}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={4}>
                    <Form.Item
                      label="Garment Piece Quantity"
                      name="garment_piece_qty"
                      rules={[
                        {
                          required: true,
                          message: "Please enter Garment Piece Quantity",
                        },
                      ]}
                    >
                      <InputNumber
                        placeholder="Garment Piece Quantity"
                        size="small"
                        min={0}
                        precision={0}
                        style={{ width: "100%" }}
                      />
                    </Form.Item>
                  </Col>
                </>
              ) : (
                <>
              <Col span={6}>
                <Form.Item label="LC Number" name="b2b_lc_id">
                  <Select
                    showSearch
                    placeholder="Search to Select"
                    optionFilterProp="children"
                    allowClear
                    size="small"
                    disabled={formData.type_of_shipment === "FOC"}
                    onChange={(value) => {
                      onChangeFormValue(value, "b2b_lc_id");
                      if (value) {
                        getInvoiceList(value);
                      }
                    }}
                  >
                    {isArrayAndHasValue(b2bLcList) &&
                      b2bLcList?.map((lcItem, index) => (
                        <Option key={lcItem?.id || index} value={lcItem?.id}>
                          {lcItem?.b2b_lc_number || ""}
                        </Option>
                      ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  label="Sales Contract"
                  name="sales_contract_id"
                  required
                >
                  <Select
                    showSearch
                    placeholder="Search to Select"
                    optionFilterProp="children"
                    allowClear
                    size="small"
                    disabled={formData.type_of_shipment !== "FOC"}
                    onChange={(value) =>
                      onChangeFormValue(value, "sales_contract_id")
                    }
                    onSearch={(value) => getSalesContractList({ name: value })}
                    filterOption={false}
                  >
                    {isArrayAndHasValue(salesContractList) &&
                      salesContractList?.map((contractItem, index) => (
                        <Option
                          key={contractItem?.id || index}
                          value={contractItem?.id}
                        >
                          {contractItem?.reference_no || ""}
                        </Option>
                      ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  label="Invoice Number"
                  name="import_details_id"
                  required
                >
                  <Select
                    showSearch
                    placeholder="Search to Select"
                    optionFilterProp="children"
                    allowClear
                    size="small"
                    disabled={!isArrayAndHasValue(invoiceList)}
                    onChange={(value) => {
                      onChangeFormValue(value, "import_details_id");
                      onSelectInvoice(value);
                    }}
                  >
                    {isArrayAndHasValue(invoiceList) &&
                      invoiceList?.map((invoiceItem, index) => (
                        <Option
                          key={invoiceItem?.id || index}
                          value={invoiceItem?.id}
                        >
                          {invoiceItem?.invoice_number || ""}
                        </Option>
                      ))}
                  </Select>
                </Form.Item>
              </Col>
                </>
              )}
              {isExport && (
                <Col span={4}>
                  <Form.Item
                    label="Exporter Name"
                    name="exporter_name"
                    rules={[
                      {
                        required: true,
                        message: "Please select Exporter Name",
                      },
                    ]}
                  >
                    <Select placeholder="Exporter Name" size="small">
                      {exporterNameOptions.map((name) => (
                        <Option key={name} value={name}>
                          {name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              )}
              {/* todo */}
              {/* <Col span={isExport ? 4 : 8}>
                <Form.Item
                  label="Loading Qty"
                  name="loading_qty"
                  rules={[
                    { required: true, message: "Please enter Loading Qty" },
                  ]}
                >
                  <InputNumber
                    placeholder="Loading Qty"
                    size="small"
                    style={{ width: "100%" }}
                    onChange={(value) =>
                      onChangeFormValue(value, "loading_qty")
                    }
                  />
                </Form.Item>
              </Col>
              <Col span={isExport ? 4 : 8}>
                <Form.Item
                  label="Loading CBM"
                  name="loading_cbm"
                  rules={[
                    { required: true, message: "Please enter Loading CBM" },
                  ]}
                >
                  <InputNumber
                    placeholder="Loading CBM"
                    size="small"
                    style={{ width: "100%" }}
                    onChange={(value) =>
                      onChangeFormValue(value, "loading_cbm")
                    }
                  />
                </Form.Item>
              </Col>
              <Col span={isExport ? 4 : 8}>
                <Form.Item
                  label={isExport ? "Gross Weight" : "Loading Weight"}
                  name="loading_weight"
                  rules={[
                    {
                      required: true,
                      message: `Please enter ${
                        isExport ? "Gross Weight" : "Loading Weight"
                      }`,
                    },
                  ]}
                >
                  <InputNumber
                    placeholder={isExport ? "Gross Weight" : "Loading Weight"}
                    size="small"
                    style={{ width: "100%" }}
                    onChange={(value) =>
                      onChangeFormValue(value, "loading_weight")
                    }
                  />
                </Form.Item>
              </Col> */}
            </Row>
          </Card>

          {/* Vehicle Assignment Table */}
          <Card title="Vehicle Assignment" style={{ marginTop: 16 }}>
            <div className="custome-table">
              <table border="1" style={{ marginBottom: "30px" }}>
                <thead>
                  <tr>
                    <th className="bc-java">Transporter</th>
                    {!isExport && (
                      <th className="bc-java">
                        {shipmentLabel}er Name
                        <strong style={{ color: "red" }}>*</strong>
                      </th>
                    )}
                    <th className="bc-java">
                      Vehicle Number<strong style={{ color: "red" }}>*</strong>
                    </th>
                    <th className="bc-java">
                      Vehicle Size<strong style={{ color: "red" }}>*</strong>
                    </th>
                    <th className="bc-java">
                      Vehicle Type<strong style={{ color: "red" }}>*</strong>
                    </th>
                    <th className="bc-java">Empty Vehicle Arrival Time</th>
                    <th className="bc-java">
                      Driver Name<strong style={{ color: "red" }}>*</strong>
                    </th>
                    <th className="bc-java">
                      Driver Cell No.<strong style={{ color: "red" }}>*</strong>
                    </th>
                    <th className="bc-java">Driving License Number</th>
                    {/* <th className="bc-java">Driver NID Number</th> */}
                    <th className="bc-java">
                      Place of Loading
                      <strong style={{ color: "red" }}>*</strong>
                    </th>
                    <th className="bc-java">
                      Place of Arrival
                      <strong style={{ color: "red" }}>*</strong>
                    </th>
                    <th className="bc-java">Arrival Date</th>
                    <th className="bc-java">Dispatch Time</th>
                    <th className="bc-java">Unloading Date Time</th>
                    <th className="bc-java">
                      Challan No.<strong style={{ color: "red" }}>*</strong>
                    </th>
                    <th className="bc-java">Gate Pass No.</th>
                    <th className="bc-java">
                      Security Bolt Seal No.
                      <strong style={{ color: "red" }}>*</strong>
                    </th>
                    {/* <th className="bc-java">GPRS Tracking Number</th> */}
                    <th className="bc-java">Add</th>
                    <th className="bc-java">Delete</th>
                  </tr>
                </thead>

                <tbody className="scrollable-table-container">
                  <Form.List name="assigned_vehicles" initialValue={[{}]}>
                    {(fields, { add, remove }) => (
                      <>
                        {fields.map((field, idx) => {
                          const vehicle =
                            form.getFieldValue("assigned_vehicles")?.[idx] ||
                            {};
                          return (
                            <tr key={field.key}>
                              <td>
                                <Form.Item
                                  {...field}
                                  name={[field.name, "transporter"]}
                                  fieldKey={[field.fieldKey, "transporter"]}
                                  rules={[]}
                                >
                                  <Select
                                    placeholder="Transporter"
                                    size="small"
                                    style={{ width: "120px" }}
                                    onChange={(value) =>
                                      onChangeVehicleValue(
                                        value,
                                        vehicle?.unique_id ?? idx,
                                        "transporter",
                                      )
                                    }
                                    value={vehicle?.transporter}
                                  >
                                    {transporter.map((option) => (
                                      <Option
                                        key={option.id}
                                        value={option.value}
                                      >
                                        {option.value}
                                      </Option>
                                    ))}
                                  </Select>
                                </Form.Item>
                              </td>
                              {!isExport && (
                                <td>
                                  <Form.Item
                                    {...field}
                                    name={[field.name, "importer_name"]}
                                    fieldKey={[field.fieldKey, "importer_name"]}
                                    rules={[
                                      {
                                        required: true,
                                        message: `Please select ${shipmentLabel}er Name`,
                                      },
                                    ]}
                                  >
                                    <Select
                                      placeholder={`${shipmentLabel}er Name`}
                                      size="small"
                                      style={{ width: "150px" }}
                                      onChange={(value) =>
                                        onChangeVehicleValue(
                                          value,
                                          vehicle?.unique_id ?? idx,
                                          "importer_name",
                                        )
                                      }
                                      value={vehicle?.importer_name}
                                    >
                                      {importer_names.map((option) => (
                                        <Option
                                          key={option.id}
                                          value={option.value}
                                        >
                                          {option.value}
                                        </Option>
                                      ))}
                                    </Select>
                                  </Form.Item>
                                </td>
                              )}
                              <td>
                                <Form.Item
                                  {...field}
                                  name={[field.name, "vehicle_number"]}
                                  fieldKey={[field.fieldKey, "vehicle_number"]}
                                  rules={[
                                    {
                                      required: true,
                                      message: "Please enter Vehicle Number",
                                    },
                                  ]}
                                >
                                  <Input
                                    placeholder="Vehicle Number"
                                    size="small"
                                    style={{ width: "100px" }}
                                    onChange={(e) =>
                                      onChangeVehicleValue(
                                        e.target.value,
                                        vehicle?.unique_id ?? idx,
                                        "vehicle_number",
                                      )
                                    }
                                    value={vehicle?.vehicle_number}
                                  />
                                </Form.Item>
                              </td>
                              <td>
                                <Form.Item
                                  {...field}
                                  name={[field.name, "vehicle_size"]}
                                  fieldKey={[field.fieldKey, "vehicle_size"]}
                                  rules={[
                                    {
                                      required: true,
                                      message: "Please select Vehicle Size",
                                    },
                                  ]}
                                >
                                  <Select
                                    placeholder="Vehicle Size"
                                    size="small"
                                    style={{ width: "80px" }}
                                    onChange={(value) =>
                                      onChangeVehicleValue(
                                        value,
                                        vehicle?.unique_id ?? idx,
                                        "vehicle_size",
                                      )
                                    }
                                    value={vehicle?.vehicle_size}
                                  >
                                    {vehicle_sizes.map((option) => (
                                      <Option
                                        key={option.value}
                                        value={option.value}
                                      >
                                        {option.value}
                                      </Option>
                                    ))}
                                  </Select>
                                </Form.Item>
                              </td>
                              <td>
                                <Form.Item
                                  {...field}
                                  name={[field.name, "vehicle_type"]}
                                  fieldKey={[field.fieldKey, "vehicle_type"]}
                                  rules={[
                                    {
                                      required: true,
                                      message: "Please select Vehicle Type",
                                    },
                                  ]}
                                >
                                  <Select
                                    showSearch
                                    placeholder="Vehicle Type"
                                    size="small"
                                    style={{ width: "150px" }}
                                    filterOption={(input, option) =>
                                      String(option?.children || "")
                                        .toLowerCase()
                                        .includes(String(input).toLowerCase())
                                    }
                                    onChange={(value) =>
                                      onChangeVehicleValue(
                                        value,
                                        vehicle?.unique_id ?? idx,
                                        "vehicle_type",
                                      )
                                    }
                                    value={vehicle?.vehicle_type}
                                    dropdownRender={(menu) => (
                                      <>
                                        {menu}
                                        <Divider style={{ margin: "8px 0" }} />
                                        <Space
                                          style={{
                                            padding: "0 8px 4px",
                                            display: "flex",
                                          }}
                                        >
                                          <Input
                                            placeholder="New type"
                                            size="small"
                                            maxLength={20}
                                            value={newVehicleTypeInput}
                                            onChange={(e) =>
                                              setNewVehicleTypeInput(
                                                e.target.value,
                                              )
                                            }
                                            onKeyDown={(e) => {
                                              e.stopPropagation();
                                              if (e.key === "Enter") {
                                                e.preventDefault();
                                                addVehicleType(
                                                  vehicle?.unique_id ?? idx,
                                                );
                                              }
                                            }}
                                          />
                                          <Button
                                            type="text"
                                            size="small"
                                            icon={<PlusOutlined />}
                                            onClick={() =>
                                              addVehicleType(
                                                vehicle?.unique_id ?? idx,
                                              )
                                            }
                                            disabled={
                                              !newVehicleTypeInput?.trim()
                                            }
                                          >
                                            Add
                                          </Button>
                                        </Space>
                                      </>
                                    )}
                                  >
                                    {vehicleTypeList.map((type) => (
                                      <Option key={type} value={type}>
                                        {type}
                                      </Option>
                                    ))}
                                  </Select>
                                </Form.Item>
                              </td>
                              <td>
                                <Form.Item
                                  {...field}
                                  name={[
                                    field.name,
                                    "empty_vehicle_arrival_time",
                                  ]}
                                  fieldKey={[
                                    field.fieldKey,
                                    "empty_vehicle_arrival_time",
                                  ]}
                                  rules={[]}
                                >
                                  <CustomDatePicker
                                    size="small"
                                    style={{ width: "140px" }}
                                    showTime
                                    format="DD-MMM-YYYY HH:mm:ss"
                                    onChange={(date) =>
                                      onChangeVehicleValue(
                                        date,
                                        vehicle?.unique_id ?? idx,
                                        "empty_vehicle_arrival_time",
                                      )
                                    }
                                    value={vehicle?.empty_vehicle_arrival_time}
                                  />
                                </Form.Item>
                              </td>
                              <td>
                                <Form.Item
                                  {...field}
                                  name={[field.name, "driver_name"]}
                                  fieldKey={[field.fieldKey, "driver_name"]}
                                  rules={[
                                    {
                                      required: true,
                                      message: "Please enter Driver Name",
                                    },
                                  ]}
                                >
                                  <Input
                                    placeholder="Driver Name"
                                    size="small"
                                    style={{ width: "100px" }}
                                    onChange={(e) =>
                                      onChangeVehicleValue(
                                        e.target.value,
                                        vehicle?.unique_id ?? idx,
                                        "driver_name",
                                      )
                                    }
                                    value={vehicle?.driver_name}
                                  />
                                </Form.Item>
                              </td>
                              <td>
                                <Form.Item
                                  {...field}
                                  name={[field.name, "driver_cell_number"]}
                                  fieldKey={[
                                    field.fieldKey,
                                    "driver_cell_number",
                                  ]}
                                  rules={[
                                    {
                                      required: true,
                                      message:
                                        "Please enter Driver Cell Number",
                                    },
                                  ]}
                                >
                                  <Input
                                    placeholder="Driver Cell No."
                                    size="small"
                                    style={{ width: "110px" }}
                                    onChange={(e) =>
                                      onChangeVehicleValue(
                                        e.target.value,
                                        vehicle?.unique_id ?? idx,
                                        "driver_cell_number",
                                      )
                                    }
                                    value={vehicle?.driver_cell_number}
                                  />
                                </Form.Item>
                              </td>
                              <td>
                                <Form.Item
                                  {...field}
                                  name={[field.name, "driving_license_number"]}
                                  fieldKey={[
                                    field.fieldKey,
                                    "driving_license_number",
                                  ]}
                                  rules={[]}
                                >
                                  <Input
                                    placeholder="Driving License"
                                    size="small"
                                    style={{ width: "110px" }}
                                    onChange={(e) =>
                                      onChangeVehicleValue(
                                        e.target.value,
                                        vehicle?.unique_id ?? idx,
                                        "driving_license_number",
                                      )
                                    }
                                    value={vehicle?.driving_license_number}
                                  />
                                </Form.Item>
                              </td>
                              {/* <td>
                                <Form.Item
                                  {...field}
                                  name={[field.name, "driver_nid_number"]}
                                  fieldKey={[
                                    field.fieldKey,
                                    "driver_nid_number",
                                  ]}
                                  rules={[]}
                                >
                                  <Input
                                    placeholder="Driver NID"
                                    size="small"
                                    style={{ width: "100px" }}
                                    onChange={(e) =>
                                      onChangeVehicleValue(
                                        e.target.value,
                                        vehicle?.unique_id ?? idx,
                                        "driver_nid_number",
                                      )
                                    }
                                    value={vehicle?.driver_nid_number}
                                  />
                                </Form.Item>
                              </td> */}
                              <td>
                                <Form.Item
                                  {...field}
                                  name={[field.name, "loading_place"]}
                                  fieldKey={[field.fieldKey, "loading_place"]}
                                  rules={[
                                    {
                                      required: true,
                                      message: "Please select Loading Place",
                                    },
                                  ]}
                                >
                                  <Select
                                    placeholder="Loading Place"
                                    size="small"
                                    style={{ width: "120px" }}
                                    onChange={(value) =>
                                      onChangeVehicleValue(
                                        value,
                                        vehicle?.unique_id ?? idx,
                                        "loading_place",
                                      )
                                    }
                                    value={vehicle?.loading_place}
                                  >
                                    {loadingPlaceOptions.map((option) => (
                                      <Option
                                        key={option.value}
                                        value={option.value}
                                      >
                                        {option.value}
                                      </Option>
                                    ))}
                                  </Select>
                                </Form.Item>
                              </td>
                              <td>
                                <Form.Item
                                  {...field}
                                  name={[field.name, "delivery_place"]}
                                  fieldKey={[field.fieldKey, "delivery_place"]}
                                  rules={[
                                    {
                                      required: true,
                                      message: "Please select Arrival Place",
                                    },
                                  ]}
                                >
                                  <Select
                                    placeholder="Arrival Place"
                                    size="small"
                                    style={{ width: "120px" }}
                                    onChange={(value) =>
                                      onChangeVehicleValue(
                                        value,
                                        vehicle?.unique_id ?? idx,
                                        "delivery_place",
                                      )
                                    }
                                    value={vehicle?.delivery_place}
                                  >
                                    {arrivalPlaceOptions.map((option) => (
                                      <Option
                                        key={option.value}
                                        value={option.value}
                                      >
                                        {option.value}
                                      </Option>
                                    ))}
                                  </Select>
                                </Form.Item>
                              </td>
                              <td>
                                <Form.Item
                                  {...field}
                                  name={[field.name, "arrival_date"]}
                                  fieldKey={[field.fieldKey, "arrival_date"]}
                                  rules={[]}
                                >
                                  <CustomDatePicker
                                    style={{ width: "140px" }}
                                    size="small"
                                    showTime
                                    format="DD-MMM-YYYY"
                                    onChange={(date) =>
                                      onChangeVehicleValue(
                                        date,
                                        vehicle?.unique_id ?? idx,
                                        "arrival_date",
                                      )
                                    }
                                    value={vehicle?.arrival_date}
                                  />
                                </Form.Item>
                              </td>
                              <td>
                                <Form.Item
                                  {...field}
                                  name={[field.name, "dispatch_time"]}
                                  fieldKey={[field.fieldKey, "dispatch_time"]}
                                  rules={[]}
                                >
                                  <CustomDatePicker
                                    size="small"
                                    style={{ width: "140px" }}
                                    showTime
                                    format="DD-MMM-YYYY HH:mm:ss"
                                    onChange={(date) =>
                                      onChangeVehicleValue(
                                        date,
                                        vehicle?.unique_id ?? idx,
                                        "dispatch_time",
                                      )
                                    }
                                    value={vehicle?.dispatch_time}
                                  />
                                </Form.Item>
                              </td>
                              <td>
                                <CustomDatePicker
                                  style={{ width: "120px" }}
                                  size="small"
                                  showTime
                                  format="DD-MMM-YYYY HH:mm:ss"
                                  disabled
                                  value={vehicle?.unloading_date}
                                />
                              </td>
                              <td>
                                <Form.Item
                                  {...field}
                                  name={[field.name, "challan_id"]}
                                  fieldKey={[field.fieldKey, "challan_id"]}
                                  rules={[
                                    {
                                      required: true,
                                      message: "Please select Challan",
                                    },
                                  ]}
                                >
                                  <Select
                                    showSearch
                                    placeholder="Challan No."
                                    optionFilterProp="children"
                                    allowClear
                                    size="small"
                                    style={{ width: "100px" }}
                                    onChange={(value) =>
                                      onChangeVehicleValue(
                                        value,
                                        vehicle?.unique_id ?? idx,
                                        "challan_id",
                                      )
                                    }
                                    onSearch={(value) =>
                                      getAllChallan({ name: value })
                                    }
                                    filterOption={false}
                                    value={vehicle?.challan_id}
                                  >
                                    {isArrayAndHasValue(challanList) &&
                                      challanList?.map((challanItem, index) => (
                                        <Option
                                          key={challanItem?.id || index}
                                          value={challanItem?.id}
                                        >
                                          {challanItem?.challan_no || ""}
                                        </Option>
                                      ))}
                                  </Select>
                                </Form.Item>
                              </td>
                              <td>
                                <Form.Item
                                  {...field}
                                  name={[field.name, "gate_pass_no"]}
                                  fieldKey={[field.fieldKey, "gate_pass_no"]}
                                  rules={[]}
                                >
                                  <Select
                                    showSearch
                                    allowClear
                                    placeholder="Gate Pass No."
                                    size="small"
                                    style={{ width: "120px" }}
                                    onChange={(value) =>
                                      onChangeVehicleValue(
                                        value,
                                        vehicle?.unique_id ?? idx,
                                        "gate_pass_no",
                                      )
                                    }
                                    value={vehicle?.gate_pass_no}
                                  >
                                    {gatePassList.map((item) => (
                                      <Option
                                        key={item.value}
                                        value={item.value}
                                      >
                                        {item.label}
                                      </Option>
                                    ))}
                                  </Select>
                                </Form.Item>
                              </td>
                              <td>
                                <Form.Item
                                  {...field}
                                  name={[
                                    field.name,
                                    "security_bolt_seal_number",
                                  ]}
                                  fieldKey={[
                                    field.fieldKey,
                                    "security_bolt_seal_number",
                                  ]}
                                  rules={[
                                    {
                                      required: true,
                                      message: "Please enter Bolt Seal No.",
                                    },
                                  ]}
                                >
                                  <Input
                                    placeholder="Bolt Seal No."
                                    size="small"
                                    style={{ width: "100px" }}
                                    onChange={(e) =>
                                      onChangeVehicleValue(
                                        e.target.value,
                                        vehicle?.unique_id ?? idx,
                                        "security_bolt_seal_number",
                                      )
                                    }
                                    value={vehicle?.security_bolt_seal_number}
                                  />
                                </Form.Item>
                              </td>
                              {/* <td>
                                <Form.Item
                                  {...field}
                                  name={[field.name, "gprs_tracking_number"]}
                                  fieldKey={[
                                    field.fieldKey,
                                    "gprs_tracking_number",
                                  ]}
                                  rules={[]}
                                >
                                  <Input
                                    placeholder="GPRS Tracking"
                                    size="small"
                                    style={{ width: "110px" }}
                                    onChange={(e) =>
                                      onChangeVehicleValue(
                                        e.target.value,
                                        vehicle?.unique_id ?? idx,
                                        "gprs_tracking_number",
                                      )
                                    }
                                    value={vehicle?.gprs_tracking_number}
                                  />
                                </Form.Item>
                              </td> */}
                              <td>
                                <Button
                                  icon={<PlusCircleOutlined />}
                                  onClick={() =>
                                    add({
                                      unique_id:
                                        (vehicle?.unique_id ?? idx) + 1,
                                    })
                                  }
                                  style={{ color: "green" }}
                                />
                              </td>
                              <td>
                                <Button
                                  icon={<MinusCircleOutlined />}
                                  onClick={() => remove(field.name)}
                                  style={{ color: "red" }}
                                  disabled={fields.length <= 1}
                                />
                              </td>
                            </tr>
                          );
                        })}
                      </>
                    )}
                  </Form.List>
                </tbody>
              </table>
            </div>
          </Card>

          <ShipmentTransportCharges isExport={isExport} />
        </Form>
      </div>
    </>
  );
});

export default ImportVehicleAssignmentForm;
